<template>
  <div class="container-fluid p-0 m-0">
    <div class="w-100">
      <div class="row wcmobile-card">
        <WCCollapseCard class="w-100 mt-2" :isCollapsedOnLoad="true">
          <template v-slot:headerTitle>
            <h4 class="my-auto text-dark font-weight-bold">{{ $t('details') }}</h4>
          </template>
          <template v-slot:cardBody>
            <WCProductHealthAttributes
              :itemAttributes="item.attributes"
              v-if="item.attributes && item.attributes.length > 0"
            />

            <ul class="list-unstyled">
              <li class="location" v-if="item.location">
                <span>{{ $t('location') }}:</span> {{ item.location }}
              </li>
              <li class="upc">
                <span>{{ $t('upc') }}:</span> {{ item.scanCode }}
              </li>
              <li class="brand" v-if="item.brand">
                <span>{{ $t('brand') }}:</span>
                <router-link
                  :aria-label="`${$t('brand')} ${item.brand}`"
                  :to="`/b?g=BRD-${item.brandId}`"
                  class="underline-link-from-center"
                >
                  {{ item.brand }}
                </router-link>
              </li>
              <li class="size" v-if="item.size">
                <span>{{ $t('size') }}:</span> {{ item.size }}
              </li>
              <li class="region" v-if="item.regionalDescriptor">
                <span>{{ $t('region') }}:</span> {{ item.regionalDescriptor }}
              </li>
              <li class="item-method" v-if="item.productionMethod">
                <span>{{ $t('productMethod') }}:</span> {{ item.productionMethod }}
              </li>
              <li class="loyalty" v-if="item.loyalty > 0">
                <span>{{ $configuration.loyaltyName }}*:</span>
                {{ item.loyalty }}
              </li>
              <li class="stock" v-if="item.availability">
                <span>{{ $t('availability') }}:</span>
                <strong v-if="item.availability === 'low'" class="low">{{
                  $configuration.limitedAvailabilityTerm
                }}</strong>
                <strong v-if="item.availability === 'showQuantity'" class="low">
                  {{ item.onHand }} {{ $configuration.inStockTerm }}</strong
                >
                <strong v-if="item.availability === 'high'" class="high">{{
                  $configuration.inStockTerm
                }}</strong>
                <strong v-if="item.availability === 'order'" class="order">{{
                  $configuration.onOrderTerm
                }}</strong>
                <strong v-if="item.availability === 'out'" class="out">{{
                  $configuration.outOfStockTerm
                }}</strong>
              </li>
              <li class="payment-options d-flex" v-if="specialTenders">
                <span>{{ $t('specialPaymentOptions') }}:</span>
                <span>{{ specialTenders }}</span>
              </li>
              <li class="pack-size" v-if="item.ascQuantity != 1">
                <span>{{ $t('packSize') }}:</span> {{ item.ascQuantity }}
              </li>
              <li class="local" v-if="item.local">
                <span>{{ $t('local') }}:</span>{{ $t('locallySourced') }}
              </li>
              <li class="pt-3" v-if="detailsCustomContent">
                <span>
                  <WCCustomContent :content="detailsCustomContent.content" />
                </span>
              </li>
            </ul>
          </template>
        </WCCollapseCard>
      </div>

      <div
        class="row wcmobile-card"
        v-if="
          item.ingredients ||
            (item.nutrition && item.nutrition.length > 0) ||
            nutritionCustomContent
        "
      >
        <WCCollapseCard class="w-100 mt-2" :isCollapsedOnLoad="true">
          <template v-slot:headerTitle>
            <h4 class="my-auto text-dark font-weight-bold">{{ $t('nutrition') }}</h4>
          </template>
          <template v-slot:cardBody>
            <div>
              <WCNutritionFacts
                v-if="item.nutrition && item.nutrition.length > 0"
                :item="item"
              ></WCNutritionFacts>
              <div v-if="item.ingredients">
                <b>{{ $t('ingredients') }}:</b> {{ item.ingredients }}
              </div>
              <div class="pt-3" v-if="nutritionCustomContent">
                <WCCustomContent :content="nutritionCustomContent.content" />
              </div>
            </div>
          </template>
        </WCCollapseCard>
      </div>

      <div class="row wcmobile-card" v-for="cs in customSourcesMinusDuplicates" :key="cs.id">
        <WCCollapseCard class="w-100 mt-2" :isCollapsedOnLoad="true">
          <template v-slot:headerTitle>
            <h4 class="my-auto text-dark font-weight-bold">{{ cs.label }}</h4>
          </template>
          <template v-slot:cardBody>
            <div v-html="cs.content"></div>
          </template>
        </WCCollapseCard>
      </div>

      <div class="row wcmobile-card">
        <WCCollapseCard class="w-100 mt-2  mb-2" :isCollapsedOnLoad="true">
          <template v-slot:headerTitle>
            <h4 class="my-auto text-dark font-weight-bold">{{ $t('disclaimer') }}</h4>
          </template>
          <template v-slot:cardBody>
            <div>
              <b>{{ $t('disclaimer') }}: </b>{{ $t('disclaimerText') }}
              {{ $configuration.store.name }} {{ $t('assumesNoLiability') }}
            </div>
            <div class="pt-3" v-if="disclaimerCustomContent">
              <WCCustomContent :content="disclaimerCustomContent.content" />
            </div>
          </template>
        </WCCollapseCard>
      </div>
      <div
        class="row wcmobile-card"
        v-if="$configuration.allowCouponClipping && displayableCoupons(item)"
      >
        <WCCollapseCard class="w-100 mt-2  mb-2" :isCollapsedOnLoad="true">
          <template v-slot:headerTitle>
            <h4 class="my-auto text-dark font-weight-bold" data-testid="coupon-collapse-card">
              {{ $t('digitalCoupons') }}
            </h4>
          </template>
          <template v-slot:cardBody>
            <div class="pt-3">
              <WCProductCardGroup class="mobile-product-card-group" role="list">
                <WCCouponCard
                  class="p-1"
                  v-for="coupon in displayableCoupons(item)"
                  :key="coupon.id"
                  :coupon="coupon"
                  role="listitem"
                />
              </WCProductCardGroup>
            </div>
          </template>
        </WCCollapseCard>
      </div>
    </div>
  </div>
</template>

<script>
import ProductDetailMixin from '@/views/ProductDetailPage/mixins/ProductDetailMixin';
import { defineComponent } from 'vue';
import CouponAvailabilityMixin from '@/modules/coupons/mixins/CouponAvailabilityMixin';
import WCNutritionFacts from '../WCNutritionFacts/WCNutritionFacts.vue';
import WCCollapseCard from '../WCCollapseCard/WCCollapseCard.vue';
import WCProductHealthAttributes from '../WCProductHealthAttributes/WCProductHealthAttributes.vue';
import WCCustomContent from '../WCCustomContent/WCCustomContent.vue';
import WCProductCardGroup from '../WCProductCardGroup/WCProductCardGroup.vue';
import WCCouponCard from '../WCCouponCard/WCCouponCard.vue';

export default defineComponent({
  components: {
    WCCollapseCard,
    WCNutritionFacts,
    WCProductHealthAttributes,
    WCCustomContent,
    WCProductCardGroup,
    WCCouponCard,
  },
  props: {
    item: {
      type: Object,
      required: true,
    },
  },
  mixins: [ProductDetailMixin, CouponAvailabilityMixin],
  data() {
    return {
      loading: false,
      loadFailedError: null,
    };
  },
  computed: {
    specialTenders() {
      return this.item.specialTenders?.map(specialTender => specialTender.name)?.join(', ');
    },
  },
});
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/tools';
@import '~@/assets/styles/settings';

.attributes {
  .attribute {
    display: inline-block;
    height: rem(30px);
    padding: calc($pad-1 / 2);
  }
}

ul li span {
  min-width: rem(140px);
  float: left;
}

.wcmobile-card {
  background: var(--light-darker-1);
}

:deep(iframe) {
  width: 100%;
}

:deep(.wc-card) {
  border: none;
  border-radius: 0;
}
</style>
